// OWNER: LG-ADMIN. Pure helpers for editing a NicheProfile (§6.1) as chips and small tables in the Niches tab:
// chip add/remove with case-insensitive dedupe, the checks shown while editing, and dirty detection. The server
// validates again (places_types against Table A, licence URLs by fetch); these checks are for instant feedback.
import { LEAD_OFFERS, type LicenceAdapter, type NicheProfile, type SoftwareVendor } from '../../../src/shared/api-leadgen';
import type { Problem } from '../admin/validate';

const err = (message: string, where: string): Problem => ({ level: 'error', where, message });
const warn = (message: string, where: string): Problem => ({ level: 'warn', where, message });

/** The plain string lists of a profile, each edited as chips. */
export type ChipField = 'search_terms' | 'places_types' | 'label_allow' | 'label_deny' | 'exclude_name_patterns' | 'review_pain_terms'
  | 'reason_lines' | 'disqualifiers';
export const CHIP_FIELDS: ReadonlyArray<{ key: ChipField; label: string; hint: string }> = [
  { key: 'search_terms', label: 'Search terms', hint: '5 to 12. Each one is a Google Maps search per area.' },
  { key: 'places_types', label: 'Google place types', hint: 'Must be in Google’s Table A list; the server removes unknown types.' },
  { key: 'label_allow', label: 'Category labels: allow', hint: 'Maps categories that are in the niche.' },
  { key: 'label_deny', label: 'Category labels: deny', hint: 'Maps categories that drop a place (gate label_deny). Labels on neither list go to the in-niche check.' },
  { key: 'exclude_name_patterns', label: 'Exclude names', hint: 'Business names to leave out.' },
  { key: 'review_pain_terms', label: 'Review pain terms', hint: 'Phone-access and niche terms the review regex looks for.' },
  { key: 'reason_lines', label: 'Reason lines', hint: 'True, checkable reasons to call (seasonal and so on).' },
  { key: 'disqualifiers', label: 'Disqualifiers', hint: 'What makes a business not a fit.' },
];

export const cleanChip = (s: string): string => s.replace(/\s+/g, ' ').trim();

/** Add one or more chips (comma or newline separated). Case-insensitive duplicates are ignored, not errors. */
export function addChips(list: string[], raw: string): string[] {
  const out = [...list];
  const seen = new Set(out.map((x) => x.toLowerCase()));
  for (const part of raw.split(/[\n,]/)) {
    const c = cleanChip(part);
    if (!c || seen.has(c.toLowerCase())) continue;
    seen.add(c.toLowerCase());
    out.push(c);
  }
  return out;
}
export const removeChip = (list: string[], i: number): string[] => list.filter((_, j) => j !== i);

export const LICENCE_ADAPTERS: readonly LicenceAdapter[] = ['agco_csv', 'ny_ocm', 'none'];
export const VENDOR_EFFECTS: ReadonlyArray<{ value: SoftwareVendor['effect']; label: string }> = [
  { value: 'disqualify', label: 'disqualify' }, { value: 'minus20', label: '−20 points' }, { value: 'minus5', label: '−5 points' }, { value: 'flag', label: 'flag only' },
];

const HOST_RE = /^(?=.{3,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;
const isHttpUrl = (s: string): boolean => { try { const u = new URL(s); return u.protocol === 'https:' || u.protocol === 'http:'; } catch { return false; } };

/** Checks while editing. Errors block Save; warnings don't. */
export function profileProblems(p: NicheProfile): Problem[] {
  const out: Problem[] = [];
  if (!cleanChip(p.display_name ?? '')) out.push(err('The profile needs a display name.', 'display_name'));
  const n = p.search_terms?.length ?? 0;
  if (n < 5 || n > 12) out.push(err(`Search terms: ${n}. A profile needs 5 to 12.`, 'search_terms'));
  if (!cleanChip(p.rank_term ?? '')) out.push(err('The rank term is empty (it drives the map-rank keywords, e.g. “plumber near me”).', 'rank_term'));
  for (const t of p.places_types ?? []) {
    if (!/^[a-z0-9_]+$/.test(t)) out.push(warn(`Place type “${t}” isn't in Google's format (lowercase_with_underscores); the server will likely drop it.`, 'places_types'));
  }
  const deny = new Set((p.label_deny ?? []).map((x) => x.toLowerCase()));
  for (const l of p.label_allow ?? []) if (deny.has(l.toLowerCase())) out.push(err(`“${l}” is on both the allow and the deny list.`, 'label_allow'));

  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(p.hours_norm?.weekday_close_threshold ?? '')) {
    out.push(err('The weekday close threshold must be a 24-hour time like 18:00.', 'hours_norm'));
  }
  if (p.review_ceiling !== null && (!Number.isInteger(p.review_ceiling) || p.review_ceiling < 1)) {
    out.push(err('The review ceiling must be a whole number of at least 1, or empty for none.', 'review_ceiling'));
  }
  const [lo, hi] = p.size_band_reviews ?? [NaN, NaN];
  if (!Number.isInteger(lo) || !Number.isInteger(hi) || lo < 0 || hi < lo) out.push(err('The review size band needs two whole numbers, low ≤ high.', 'size_band_reviews'));

  const offers = (p.offers_ranked ?? []).map((o) => o.offer);
  if (offers.length === 0) out.push(err('Rank at least one offer.', 'offers_ranked'));
  if (new Set(offers).size !== offers.length) out.push(err('An offer is ranked twice.', 'offers_ranked'));
  for (const o of offers) if (!LEAD_OFFERS.includes(o)) out.push(err(`Unknown offer “${o}”.`, 'offers_ranked'));

  (p.software_vendors ?? []).forEach((v, i) => {
    if (!cleanChip(v.name)) out.push(err(`Software vendor ${i + 1} needs a name.`, 'software_vendors'));
    if (!HOST_RE.test(v.host.trim())) out.push(err(`Software vendor “${v.name || i + 1}”: “${v.host}” isn't a hostname like app.example.com.`, 'software_vendors'));
  });
  const unverifiedVendors = (p.software_vendors ?? []).filter((v) => !v.verified).length;
  if (unverifiedVendors) out.push(warn(`${unverifiedVendors} software vendor${unverifiedVendors === 1 ? '' : 's'} not verified yet. Check the host before relying on it.`, 'software_vendors'));
  const unconfirmedBrands = (p.chain_brands ?? []).filter((b) => !b.verified).length;
  if (unconfirmedBrands) out.push(warn(`${unconfirmedBrands} chain brand${unconfirmedBrands === 1 ? '' : 's'} still to confirm.`, 'chain_brands'));

  (p.licence_sources ?? []).forEach((s, i) => {
    if (!/^(US|CA)-[A-Z]{2}$/.test(s.jurisdiction)) out.push(err(`Licence source ${i + 1}: jurisdiction must look like CA-ON or US-NY.`, 'licence_sources'));
    if (!isHttpUrl(s.url)) out.push(err(`Licence source ${s.jurisdiction || i + 1}: the URL must start with https://.`, 'licence_sources'));
  });
  if (p.licence_required && !(p.licence_sources ?? []).some((s) => s.adapter !== 'none')) {
    out.push(err('This niche needs a licence, but no source has an adapter. No licence data, no search.', 'licence_sources'));
  }
  if (p.healthcare_ca_risk?.flag) out.push(warn(`Blocked for Canada (CRTC): ${p.healthcare_ca_risk.reason || 'healthcare'}.`, 'healthcare_ca_risk'));
  return out;
}

/** Stable JSON for dirty detection (key order as given by the server or by our edits). */
export const profileJson = (p: NicheProfile): string => JSON.stringify(p);
export const cloneProfile = (p: NicheProfile): NicheProfile => JSON.parse(JSON.stringify(p)) as NicheProfile;
